import { fetchGroupsByCourseId } from '../admin';
import { getAllEventData, getAssessmentsByEvent } from '../firebase';

// Example report format:

// TEAM NAME (e.g., Discussion 1 Team 2)
// TOTAL NUMBER OF ACTIVITIES (e.g., 32)
// TEAM MEMBER NUMBER 1 NAME:
// TOTAL NUMBER OF WORK ASSESSMENTS THEY MADE + THE PERCENTAGE (e.g., 28, 89%)
// TOTAL NUMBER OF WORK ASSESSMENTS THEY RECEIVED + THE PERCENTAGE (e.g., 4, 14%)
// DIFFICULT SCORE GIVEN: #1, #2, #3, #4, AVG + STDDEV (e.g., 6 1’s, 7 2’s, 13 3’s, 0 4’s, 2.35, 0.2)
// AVERAGE QUALITY SCORE GIVEN + STANDARD DEVIATION (e.g., …)
// AVERAGE VOLUME SCORE GIVEN + STANDARD DEVIATION (e.g., …)
// DIFFICULT SCORE RECEIVED: #1, #2, #3, #4, AVG + STDDEV (e.g., 6 1’s, 7 2’s, 13 3’s, 0 4’s, 2.35, 0.2)
// AVERAGE QUALITY SCORE RECEIVED + STANDARD DEVIATION (e.g., …)
// AVERAGE VOLUME SCORE RECEIVED + STANDARD DEVIATION (e.g., …)
// TEAM MEMBER NUMBER 2 NAME:
// ...
 
// NEXT TEAM
// <<etc>>


function mean(values) {
    if (values.length === 0) return 0;
    return values.reduce((a, b) => a + b, 0) / values.length;
}

function stddev(values) {
    if (values.length === 0) return 0;
    const m = mean(values);
    return Math.sqrt(values.reduce((sum, v) => sum + (v - m) ** 2, 0) / values.length);
}

function fmt(n) {
    return n.toFixed(2);
}

function dist(scores) {
    const counts = [0, 0, 0, 0];
    for (const s of scores) {
        if (s >= 1 && s <= 4) counts[s - 1]++;
    }
    return counts;
}

function distLine(label, scores) {
    const d = dist(scores);
    const m = mean(scores);
    const sd = stddev(scores);
    return `${label}: ${d[0]} 1's, ${d[1]} 2's, ${d[2]} 3's, ${d[3]} 4's, ${fmt(m)}, ${fmt(sd)}`;
}

function scoreLine(label, scores) {
    return `${label}: ${fmt(mean(scores))}, ${fmt(stddev(scores))}`;
}

function getContributorEmails(event) {
    if (Array.isArray(event.contributorIds) && event.contributorIds.length) {
        return event.contributorIds;
    }
    if (Array.isArray(event.contributors)) {
        return event.contributors.map(c => (typeof c === 'string' ? c : c.email)).filter(Boolean);
    }
    return [];
}

const CHUNK_SIZE = 10;

async function fetchAllAssessments(eventIds) {
    const results = [];
    for (let i = 0; i < eventIds.length; i += CHUNK_SIZE) {
        const chunk = eventIds.slice(i, i + CHUNK_SIZE);
        const chunkResults = await Promise.all(
            chunk.map(async eventId => {
                const assessments = await getAssessmentsByEvent(eventId);
                return assessments.map(a => ({ ...a, _eventId: eventId }));
            })
        );
        results.push(...chunkResults.flat());
    }
    return results;
}

async function buildGroupLines(group) {
    const members = group.members || [];
    const groupName = group.groupName || group.id;

    const events = await getAllEventData(group.id);
    const eventDataMap = new Map(events.map(e => [e.eventId || e.id, e]));
    const eventIds = [...eventDataMap.keys()];

    const allAssessments = await fetchAllAssessments(eventIds);

    const totalActivities = eventIds.length;
    const lines = [];

    lines.push(`TEAM NAME: ${groupName}`);
    lines.push(`TOTAL NUMBER OF ACTIVITIES: ${totalActivities}`);
    lines.push('');

    members.forEach((member, idx) => {
        const email = member.email;
        const name = member.name || email;

        const given = allAssessments.filter(a => a.raterEmail === email);
        const givenCount = given.length;
        const givenPct = totalActivities > 0
            ? ((givenCount / totalActivities) * 100).toFixed(1)
            : '0.0';

        const received = allAssessments.filter(a => {
            const event = eventDataMap.get(a._eventId);
            return event ? getContributorEmails(event).includes(email) : false;
        });
        const receivedCount = received.length;

        const activitiesContributed = eventIds.filter(eid => {
            const event = eventDataMap.get(eid);
            return event ? getContributorEmails(event).includes(email) : false;
        }).length;
        const shouldReceive = activitiesContributed * members.length;
        const receivedPct = shouldReceive > 0
            ? ((receivedCount / shouldReceive) * 100).toFixed(1)
            : '0.0';

        const g = {
            importance: given.map(a => a.memberRatings?.importance?.score).filter(Boolean),
            quality: given.map(a => a.memberRatings?.quality?.score).filter(Boolean),
            volume: given.map(a => a.memberRatings?.volume?.score).filter(Boolean),
        };
        const r = {
            importance: received.map(a => a.memberRatings?.importance?.score).filter(Boolean),
            quality: received.map(a => a.memberRatings?.quality?.score).filter(Boolean),
            volume: received.map(a => a.memberRatings?.volume?.score).filter(Boolean),
        };

        lines.push(`TEAM MEMBER NUMBER ${idx + 1} NAME: ${name}`);
        lines.push(`  TOTAL NUMBER OF WORK ASSESSMENTS THEY MADE: ${givenCount}, ${givenPct}%`);
        lines.push(`  TOTAL NUMBER OF WORK ASSESSMENTS THEY RECEIVED: ${receivedCount}, ${receivedPct}%`);
        lines.push('  ' + distLine('Nature SCORE GIVEN', g.importance));
        lines.push('  ' + scoreLine('AVERAGE QUALITY SCORE GIVEN', g.quality));
        lines.push('  ' + scoreLine('AVERAGE VOLUME SCORE GIVEN', g.volume));
        lines.push('  ' + distLine('Nature SCORE RECEIVED', r.importance));
        lines.push('  ' + scoreLine('AVERAGE QUALITY SCORE RECEIVED', r.quality));
        lines.push('  ' + scoreLine('AVERAGE VOLUME SCORE RECEIVED', r.volume));
        lines.push('');
    });

    lines.push('='.repeat(60));
    lines.push('');
    return lines;
}

export async function generateCourseReport(courseId) {
    const groups = await fetchGroupsByCourseId(courseId);
    groups.sort((a, b) =>
        (a.groupName || '').localeCompare(b.groupName || '', undefined, { numeric: true, sensitivity: 'base' })
    );

    const groupLines = await Promise.all(groups.map(buildGroupLines));
    return groupLines.flat().join('\n');
}

export function downloadTextFile(text, filename) {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
